import { X, Warning, Bug, PaperPlaneTilt, Check, Spinner } from '@phosphor-icons/react'
import { Button } from './ui/button'
import { useState } from 'react'
import api from '../services/api'

interface ErrorModalProps {
  isOpen: boolean
  onClose: () => void
  error: {
    message: string
    details?: string
    code?: string
    endpoint?: string
  }
  context?: {
    page: string
    action: string
    userId?: string
    companyId?: string
    additionalInfo?: Record<string, unknown>
  }
}

export function ErrorModal({ isOpen, onClose, error, context }: ErrorModalProps) {
  const [reportSent, setReportSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [reportError, setReportError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleReportError = async () => {
    try {
      setSending(true)
      setReportError(null)

      await api.post('/error-report', {
        error: {
          message: error.message,
          details: error.details,
          code: error.code,
          endpoint: error.endpoint,
        },
        context: {
          ...context,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
      })

      setReportSent(true)
    } catch (err) {
      console.error('Failed to send error report:', err)
      setReportError('Falha ao enviar relatório. Tente novamente.')
    } finally {
      setSending(false)
    }
  }

  const handleClose = () => {
    setReportSent(false)
    setReportError(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <Warning size={24} className="text-red-600" weight="fill" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Ops! Algo deu errado</h2>
              <p className="text-sm text-gray-500">Erro ao processar sua solicitação</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-red-100 transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-medium text-gray-900 mb-1">Mensagem do erro:</p>
            <p className="text-sm text-gray-600">{error.message}</p>

            {error.details && (
              <>
                <p className="text-sm font-medium text-gray-900 mt-3 mb-1">Detalhes técnicos:</p>
                <p className="text-xs text-gray-500 font-mono bg-gray-100 p-2 rounded overflow-x-auto">
                  {error.details}
                </p>
              </>
            )}

            {error.code && (
              <p className="text-xs text-gray-400 mt-2">
                Código: <span className="font-mono">{error.code}</span>
              </p>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Lamentamos o inconveniente. Você pode tentar novamente ou reportar este erro
            para nossa equipe técnica investigar.
          </p>

          {reportSent ? (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
              <Check size={20} weight="bold" />
              <span className="text-sm font-medium">
                Relatório enviado! Nossa equipe foi notificada.
              </span>
            </div>
          ) : (
            <>
              {reportError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-3">
                  <Warning size={18} />
                  <span className="text-sm">{reportError}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose}>
            Fechar
          </Button>

          {!reportSent && (
            <Button
              onClick={handleReportError}
              disabled={sending}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            >
              {sending ? (
                <>
                  <Spinner size={18} className="animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Bug size={18} />
                  Reportar Erro
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
